import omit from 'lodash.omit';

import { getAuthenticatedUser } from '../AuthenticationService';
import ConsentService, { ConsentCategory } from '../ConsentService';
import * as featureFlagService from '../FeatureFlagService';

import { PROJECTS } from './constants';

const CONSENT_CATEGORY = ConsentCategory.Preferences;

export let api = null;
export const getAPI = () => {
  return api;
};
export const setAPI = (newAPI) => {
  api = newAPI;
};

const isEnabled = () => {
  const KEY = environment.USERSNAP_GLOBAL_KEY;
  return Boolean(KEY);
};

export const ensureAPI = () => {
  if (!isEnabled() || !ConsentService.isCategoryConsented(CONSENT_CATEGORY)) {
    return Promise.resolve({
      show: () => Promise.resolve(),
      hide: () => Promise.resolve(),
      logEvent: () => Promise.resolve(),
      _fake: true,
    });
  }

  if (getAPI()) {
    return Promise.resolve(getAPI());
  }

  return new Promise((resolve, reject) => {
    window.onUsersnapCXLoad = (usersnapAPI) => {
      usersnapAPI.init({
        user: {
          userId: getAuthenticatedUser().uuid,
          email: window.TLGLOBALS.EMAIL,
        },
      });
      setAPI(usersnapAPI);
      delete window.onUsersnapCXLoad;
      resolve(getAPI());
    };

    const KEY = environment.USERSNAP_GLOBAL_KEY;

    const script = document.createElement('script');
    script.defer = true;
    script.src = `https://widget.usersnap.com/global/load/${KEY}?onload=onUsersnapCXLoad`;
    script.type = ConsentService.isEnabled ? 'text/plain' : 'text/javascript';
    script.dataset.cookieconsent = CONSENT_CATEGORY;
    script.onerror = (error) => {
      reject(error);
    };
    document.body.appendChild(script);
    ConsentService.runScripts();
  });
};

export let projects = PROJECTS;
export const getProjects = () => {
  return projects;
};
export const setProjects = (newProjects) => {
  projects = newProjects;
};

export const show = (projectKey) =>
  ensureAPI().then((api) => {
    api
      .show(projectKey)
      .then(() => {
        setProjects({
          ...getProjects(),
          [projectKey]: { ...getProjects()[projectKey], active: true },
        });
      })
      // eslint-disable-next-line node/handle-callback-err
      .catch((error) => {
        // Ignore usersnap error when projects are paused
      });
  });

export const hide = (projectKey) =>
  ensureAPI().then((api) => {
    api.hide(projectKey).then(() => {
      setProjects({
        ...getProjects(),
        [projectKey]: { ...getProjects()[projectKey], active: false },
      });
    });
  });

export const registerProject = (projectKey, route, featureFlag) => {
  setProjects({ ...getProjects(), [projectKey]: { route, featureFlag, active: false } });
  // To include also hash and search
  const activeRoute = window.location.href.split(window.location.host)[1];
  activateBasedOnRoute(activeRoute);
};

export const unregisterProject = (projectKey) => {
  const project = getProjects()[projectKey];
  const removeProject = () => setProjects(omit(projects, projectKey));
  if (project.active) {
    return hide(projectKey).then(() => removeProject());
  }

  return Promise.resolve(removeProject());
};

export const dispatch = (event) => {
  return ensureAPI().then((api) => api.logEvent(event));
};

export const activateBasedOnRoute = (route) => {
  const projects = getProjects();
  const changedProjects = Object.keys(projects)
    .map((projectKey) => {
      const project = projects[projectKey];

      if (!('route' in project)) {
        return false;
      }

      if (
        project.featureFlag &&
        !(featureFlagService.get(project.featureFlag) || featureFlagService.getAccountFeatureFlag(project.featureFlag))
      ) {
        return false;
      }

      const regex = project.route;
      const matchesCurrentRoute = regex.test(route);
      if (matchesCurrentRoute && !project.active) {
        return show(projectKey);
      }

      if (!matchesCurrentRoute && project.active) {
        return hide(projectKey);
      }

      return false;
    })
    .filter(Boolean);

  return Promise.all(changedProjects);
};
